import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Recipe, RecipeFilters } from '@coffeeplexity/shared';
import { recipeApi, CreateRecipeData } from '../../services/api/recipeApi';
import { logger } from '../../utils/logger';

interface RecipeState {
  recipes: Recipe[];
  featuredRecipes: Recipe[];
  userRecipes: Recipe[];
  currentRecipe: Recipe | null;
  isLoading: boolean;
  error: string | null;
  filters: Partial<RecipeFilters>;
  pagination: {
    page: numb;
    er;
    limit: number;
    total: number;
    hasMore: boolean;
  };
}

const initialState: RecipeState = {
  recipes: [],
  featuredRecipes: [],
  userRecipes: [],
  currentRecipe: null,
  isLoading: false,
  error: null,
  filters: {},
  pagination: {
    page: 1,
    limit: 12,
    total: 0,
    hasMore: true,
  },
};

// Async thunks
export const fetchRecipes = createAsyncThunk(
  'recipes/fetchRecipes',
  async (
    params: { page?: number; filters?: Partial<RecipeFilters> } = {},
    { rejectWithValue }
  ) => {
    try {
      const response = await recipeApi.getRecipes(params);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || 'Failed to fetch recipes'
      );
    }
  }
);

export const fetchFeaturedRecipes = createAsyncThunk(
  'recipes/fetchFeaturedRecipes',
  async (_, { rejectWithValue }) => {
    try {
      const response = await recipeApi.getFeaturedRecipes();
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || 'Failed to fetch featured recipes'
      );
    }
  }
);

export const fetchRecipeById = createAsyncThunk(
  'recipes/fetchRecipeById',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await recipeApi.getRecipeById(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || 'Failed to fetch recipe'
      );
    }
  }
);

export const createRecipe = createAsyncThunk(
  'recipes/createRecipe',
  async (recipeData: CreateRecipeData, { rejectWithValue }) => {
    try {
      const response = await recipeApi.createRecipe(recipeData);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || 'Failed to create recipe'
      );
    }
  }
);

export const updateRecipe = createAsyncThunk(
  'recipes/updateRecipe',
  async (
    { id, data }: { id: string; data: Partial<CreateRecipeData> },
    { rejectWithValue }
  ) => {
    try {
      const response = await recipeApi.updateRecipe(id, data);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || 'Failed to update recipe'
      );
    }
  }
);

export const deleteRecipe = createAsyncThunk(
  'recipes/deleteRecipe',
  async (id: string, { rejectWithValue }) => {
    try {
      await recipeApi.deleteRecipe(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || 'Failed to delete recipe'
      );
    }
  }
);

export const rateRecipe = createAsyncThunk(
  'recipes/rateRecipe',
  async (
    { recipeId, rating }: { recipeId: string; rating: number },
    { rejectWithValue }
  ) => {
    try {
      const response = await recipeApi.rateRecipe(recipeId, rating);
      return { recipeId, rating: response.rating };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || 'Failed to rate recipe'
      );
    }
  }
);

export const fetchUserRecipes = createAsyncThunk(
  'recipes/fetchUserRecipes',
  async (
    params: {
      type?: 'created' | 'bookmarked' | 'liked';
      page?: number;
      limit?: number;
    } = {},
    { rejectWithValue }
  ) => {
    try {
      const response = await recipeApi.getUserRecipes(params);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || 'Failed to fetch user recipes'
      );
    }
  }
);

export const toggleFavorite = createAsyncThunk(
  'recipes/toggleFavorite',
  async (
    { recipeId, userId }: { recipeId: string; userId: string },
    { rejectWithValue }
  ) => {
    try {
      // Check if recipe is already bookmarked by trying to fetch user's bookmarked recipes
      // Note: This is a workaround since we don't have a direct endpoint for checking bookmark status
      // In production, consider adding a GET /recipes/{id}/interactions endpoint
      const userRecipes = await recipeApi.getUserRecipes({
        type: 'bookmarked',
        creator_id: userId,
      });
      const isBookmarked = userRecipes.recipes.some(
        recipe => recipe.id === recipeId
      );

      if (isBookmarked) {
        await recipeApi.unbookmarkRecipe(recipeId);
        return { recipeId, isFavorite: false };
      } else {
        await recipeApi.bookmarkRecipe(recipeId);
        return { recipeId, isFavorite: true };
      }
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || 'Failed to toggle favorite'
      );
    }
  }
);

const recipeSlice = createSlice({
  name: 'recipes',
  initialState,
  reducers: {
    clearError: state => {
      state.error = null;
    },
    setFilters: (state, action: PayloadAction<Partial<RecipeFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
      state.pagination.page = 1;
    },
    clearFilters: state => {
      state.filters = {};
      state.pagination.page = 1;
    },
    setCurrentRecipe: (state, action: PayloadAction<Recipe | null>) => {
      state.currentRecipe = action.payload;
    },
    clearRecipes: state => {
      state.recipes = [];
      state.pagination = initialState.pagination;
    },
  },
  extraReducers: builder => {
    builder
      // Fetch Recipes
      .addCase(fetchRecipes.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchRecipes.fulfilled, (state, action) => {
        state.isLoading = false;
        const { recipes, pagination } = action.payload;
        if (pagination.page === 1) {
          state.recipes = recipes;
        } else {
          state.recipes = [...state.recipes, ...recipes];
        }
        state.pagination = pagination;
      })
      .addCase(fetchRecipes.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch Featured Recipes
      .addCase(fetchFeaturedRecipes.pending, state => {
        state.isLoading = true;
      })
      .addCase(fetchFeaturedRecipes.fulfilled, (state, action) => {
        state.isLoading = false;
        state.featuredRecipes = action.payload;
      })
      .addCase(fetchFeaturedRecipes.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch Recipe by ID
      .addCase(fetchRecipeById.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchRecipeById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentRecipe = action.payload;
      })
      .addCase(fetchRecipeById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch User Recipes
      .addCase(fetchUserRecipes.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUserRecipes.fulfilled, (state, action) => {
        state.isLoading = false;
        state.userRecipes = action.payload.recipes;
      })
      .addCase(fetchUserRecipes.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Create Recipe
      .addCase(createRecipe.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createRecipe.fulfilled, (state, action) => {
        state.isLoading = false;
        state.recipes.unshift(action.payload);
      })
      .addCase(createRecipe.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Update Recipe
      .addCase(updateRecipe.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateRecipe.fulfilled, (state, action) => {
        state.isLoading = false;
        const updatedRecipe = action.payload;
        state.recipes = state.recipes.map(recipe =>
          recipe.id === updatedRecipe.id ? updatedRecipe : recipe
        );
        if (state.currentRecipe?.id === updatedRecipe.id) {
          state.currentRecipe = updatedRecipe;
        }
      })
      .addCase(updateRecipe.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Delete Recipe
      .addCase(deleteRecipe.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteRecipe.fulfilled, (state, action) => {
        state.isLoading = false;
        const deletedId = action.payload;
        state.recipes = state.recipes.filter(recipe => recipe.id !== deletedId);
        state.userRecipes = state.userRecipes.filter(
          recipe => recipe.id !== deletedId
        );
        if (state.currentRecipe?.id === deletedId) {
          state.currentRecipe = null;
        }
      })
      .addCase(deleteRecipe.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Rate Recipe
      .addCase(rateRecipe.fulfilled, (state, action) => {
        const { recipeId, rating } = action.payload;
        // Simple rating update without complex type checking
        state.recipes = state.recipes.map(recipe =>
          recipe.id === recipeId ? { ...recipe, rating } : recipe
        );
        if (state.currentRecipe?.id === recipeId) {
          state.currentRecipe = { ...state.currentRecipe, rating };
        }
      })
      // Toggle Favorite
      .addCase(toggleFavorite.fulfilled, (state, action) => {
        const { recipeId, isFavorite } = action.payload;
        // Note: Recipe type doesn't have is_favorite property
        // We could extend the state to track favorites separately if needed
        // For now, we'll just log the action for debugging
        logger.log(`Recipe ${recipeId} favorite status: ${isFavorite}`);
      });
  },
});

export const {
  clearError,
  setFilters,
  clearFilters,
  setCurrentRecipe,
  clearRecipes,
} = recipeSlice.actions;

// Selectors
export const selectRecipes = (state: { recipes: RecipeState }) =>
  state.recipes.recipes;
export const selectFeaturedRecipes = (state: { recipes: RecipeState }) =>
  state.recipes.featuredRecipes;
export const selectUserRecipes = (state: { recipes: RecipeState }) =>
  state.recipes.userRecipes;
export const selectCurrentRecipe = (state: { recipes: RecipeState }) =>
  state.recipes.currentRecipe;
export const selectRecipesLoading = (state: { recipes: RecipeState }) =>
  state.recipes.isLoading;
export const selectRecipesError = (state: { recipes: RecipeState }) =>
  state.recipes.error;
export const selectRecipesFilters = (state: { recipes: RecipeState }) =>
  state.recipes.filters;
export const selectRecipesPagination = (state: { recipes: RecipeState }) =>
  state.recipes.pagination;

export default recipeSlice.reducer;
